import React, { useEffect, useState } from "react";
import { ClearRestoreEntraIdUserTracking, RestoreEntraIdUserTracking } from "../../../../store/TrackingOperationAction";
import { CoreTracking } from "../../../../models/CoreTracking";
import { useDispatch, useSelector } from "react-redux";
import { RequestState } from "../../../../models/RequestState";
import { CoreEntraIdUserTrackingConflict } from "../../../../models/CoreTrackingOperation";
import StatusCode from "../../../../util/StatusCode";
import ModalComponent from "../../../misc/ModalComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import EntraIdUserTrackingOperationsValidationResult from "./EntraIdUserTrackingOperationsValidationResult";
import CoreApiError from "../../../misc/CoreApiError";
import Loading from "../../../misc/Loading";
import { EntraIdUserTrackingMetadata } from "../../../../models/TrackingMetadata";
import { useTranslation } from 'react-i18next';
import { RootState } from "../../../../store/store";



interface RestoreEntraIdUserTrackingWizardProps {
    tracking: CoreTracking
    close: (success: boolean) => void
}

const RestoreEntraIdUserTrackingWizard: React.FC<RestoreEntraIdUserTrackingWizardProps> = (props) => {
    const dispatch = useDispatch();
    const metadata = JSON.parse(props.tracking.metadata) as EntraIdUserTrackingMetadata;
    const [wizardStep, setWizardStep] = useState<'confirm' | 'done'>('confirm');
    const restoreEntraIdUserTracking = useSelector<RootState, RequestState<CoreEntraIdUserTrackingConflict[]>>(state => state.trackingOperation.restoreEntraIdUserTracking);
    const { t } = useTranslation(['schoolAdministration', 'common']);

    useEffect(() => {
        RestoreEntraIdUserTracking(props.tracking.trackingId, true)(dispatch);
        return () => ClearRestoreEntraIdUserTracking()(dispatch);
    }, [props, dispatch]);

    const confirmInput: React.MouseEventHandler<HTMLButtonElement> = (_) => {
        RestoreEntraIdUserTracking(props.tracking.trackingId, false)(dispatch);
        setWizardStep('done');
    }

    const close = (success: boolean) => {
        setWizardStep('confirm');
        props.close(success);
    }

    switch (wizardStep) {
        case 'confirm': {
            if (restoreEntraIdUserTracking.code === StatusCode.COMPLETE) {
                if (restoreEntraIdUserTracking.data.length === 0) {
                    return <ModalComponent
                        isOpen={true}
                        toggleModal={() => close(false)}
                        header={t('trackingOperations.entraId.restore.modalTitle', {ns: 'schoolAdministration'})}
                        update={confirmInput}
                        confirm={t('action.confirm', {ns: 'common'})}
                        cancel={t('action.cancel', {ns: 'common'})}>
                        <p><FontAwesomeIcon title={t('status.OK', {ns: 'common'})} color="green" icon={['fas', 'check-circle']} />{t('trackingOperations.entraId.restore.confirm', {ns: 'schoolAdministration'})}</p>
                    </ModalComponent>
                }

                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.entraId.restore.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}>
                    <p><FontAwesomeIcon title={t('status.error', {ns: 'common'})} color="red" icon={['fas', 'triangle-exclamation']} />{t('trackingOperations.general.conflictsDiscovered', {ns: 'schoolAdministration'})}</p>
                    <EntraIdUserTrackingOperationsValidationResult entraIdId={props.tracking.sourceSystemId} userPrincipalName={metadata.UserPrincipalName.Value} conflicts={restoreEntraIdUserTracking.data} />
                </ModalComponent>;
            } else if (restoreEntraIdUserTracking.code === StatusCode.ERROR) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.entraId.restore.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}><CoreApiError  requestState={restoreEntraIdUserTracking} /></ModalComponent>
            } else {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.entraId.restore.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}>
                    <Loading />
                </ModalComponent>;
            }
        }
        case 'done': {
            if (restoreEntraIdUserTracking.code === StatusCode.COMPLETE) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(true)}
                    header={t('trackingOperations.entraId.restore.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.close', {ns: 'common'})}>{t('trackingOperations.general.restoreCompleted', {ns: 'schoolAdministration'})}</ModalComponent>
            } else if (restoreEntraIdUserTracking.code === StatusCode.ERROR) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.entraId.restore.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.close', {ns: 'common'})}><CoreApiError  requestState={restoreEntraIdUserTracking} /></ModalComponent>
            } else {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.entraId.restore.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.close', {ns: 'common'})}>
                    <Loading />
                </ModalComponent>;
            }
        }
    }
}

export default RestoreEntraIdUserTrackingWizard;
